import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { FormBuilder, Validators } from "@angular/forms";
import { MatSnackBar } from "@angular/material/snack-bar";
import { TranslateService } from "@ngx-translate/core";
import { Subject, finalize, first, takeUntil } from "rxjs";
import { LanguageService } from "src/app/shared/services/language.service";
import { OrganizationCreationErrorCode } from "../../domain/interfaces/organization.interface";
import { OrganizationService } from "../../domain/services/organization.service";

@Component({
  selector: "app-add-organization-page",
  templateUrl: "./add-organization-page.component.html",
  styleUrls: ["./add-organization-page.component.scss"],
})
export class AddOrganizationPageComponent implements OnInit, OnDestroy {
  /**
   * Scrolls the success card into view as soon as it appears, so the user notices it
   * even if they were scrolled down further in the form when submitting.
   */
  @ViewChild("successCard")
  set successCardSetter(el: ElementRef<HTMLDivElement> | undefined) {
    if (el) {
      el.nativeElement.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }

  public languages: string[] = [];
  public isSuccess = false;
  public successOrganizationName = "";
  public emailAlreadyUsed = false;
  public organizationNameAlreadyUsed = false;

  public organizationForm = this.fb.nonNullable.group({
    organization: this.fb.nonNullable.group({
      name: ["", Validators.required],
      address: ["", Validators.required],
      addressNb: ["", Validators.required],
      postalCode: ["", Validators.required],
      location: ["", Validators.required],
      country: ["", Validators.required],
    }),
    admin: this.fb.nonNullable.group({
      email: ["", [Validators.required, Validators.pattern(/^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/)]],
      lang: [this.translateService.currentLang, Validators.required],
    }),
  });

  protected onDestroy$ = new Subject<void>();

  constructor(
    private readonly translateService: TranslateService,
    private readonly organizationService: OrganizationService,
    private readonly fb: FormBuilder,
    private readonly snackBar: MatSnackBar,
    private readonly languageService: LanguageService
  ) {}

  /**
   * Loads the list of available languages for the admin's language field.
   */
  ngOnInit(): void {
    this.languageService
      .getLanguages()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((res) => {
        this.languages = res.map((language) => language.code);
      });
  }

  /**
   * Emits onDestroy$ to terminate all active subscriptions.
   */
  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  /**
   * Validates and submits the organization creation form, showing a success card on
   * completion or surfacing field-specific errors when the email or organization name is
   * already taken.
   */
  public submit(): void {
    this.isSuccess = false;

    if (this.organizationForm.invalid) {
      this.organizationForm.markAllAsTouched();
      return;
    }

    this.organizationForm.disable();

    const orgCreationDetails = this.organizationForm.getRawValue();

    this.organizationService
      .createOrganization(orgCreationDetails)
      .pipe(
        first(),
        takeUntil(this.onDestroy$),
        finalize(() => this.organizationForm.enable())
      )
      .subscribe({
        next: () => {
          this.successOrganizationName = orgCreationDetails.organization.name;
          this.isSuccess = true;
          this.emailAlreadyUsed = false;
          this.organizationNameAlreadyUsed = false;
          this.organizationForm.reset({
            organization: {
              name: "",
              address: "",
              addressNb: "",
              postalCode: "",
              location: "",
              country: "",
            },
            admin: {
              email: "",
              lang: this.translateService.currentLang,
            },
          });
        },
        error: (err) => {
          if (err.status === 409 && err.error === OrganizationCreationErrorCode.EMAIL_TAKEN) {
            this.emailAlreadyUsed = true;
            return;
          } else if (err.status === 409 && err.error === OrganizationCreationErrorCode.ORGANIZATION_NAME_TAKEN) {
            this.organizationNameAlreadyUsed = true;
            return;
          } else {
            this.snackBar.open(`❗ ${this.translateService.instant("common.error")}`, "OK", {
              duration: 5000,
            });
          }
        },
      });
  }
}
